import * as React from 'react';
import { Trans, withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import * as moment from 'moment';
import * as R from 'ramda';

import ProfileImage from '@common/components/profile-image';
import mergeNamesText from '@common/utils/merged-names-text';
import Overview from '@common/components/overview';
import { Table } from '@common/components/table';
import Dropdown from '@common/components/dropdown';
import Confirm from '@common/components/confirm-button';
import Container from '@common/components/container';
import SearchBar from '@common/components/search-bar';
import { Button } from '@common/components/button';
import AdminsForm from '@modules/admin/forms/admins';
import AdminForm from '@modules/admin/forms/admin';

import { EPermissions } from '@common/definitions';
import LinkWrapper from '@common/components/linkWrapper';
import { updateUserRoles } from '../../actions';
import * as userSelector from '../../../core/selectors/logged-user';
import * as networkSelector from '../../selectors/network';
import { pageWrapper, EEventNames } from '../../../../../client/analytics';

require('./admins.scss');

const NetworkSettingsAdminsContainer = ({
  loggedUser, network, roles, permissions, admins, fetchRoles, fetchAdmins, fetchPermissionGroups, removeAdmin, t,
}) => {
  const [search, setSearch] = React.useState(null);
  const [editing, setEditing] = React.useState(null);

  const columns = [
    { size: 35 },
    { label: t('organisation:settings_admins_name') },
    { label: t('organisation:settings_admins_last_edited') },
    { label: t('organisation:settings_admins_roles') },
  ];

  React.useEffect(() => {
    fetchRoles();
  }, []);

  const admin = R.find(R.propEq('id', editing), admins);

  return (
    <Container>
      {admin && (
        <AdminForm
          show={!!editing}
          admin={admin}
          roles={roles}
          network={network}
          onClose={() => setEditing(null)}
          fetchGroups={fetchPermissionGroups}
          updateAdmin={updateUserRoles}
          removeAdmin={removeAdmin}
        />
      )}
      <Container.Content>
        <Overview>
          <Overview.Header>
            <Overview.Header.Title><Trans i18nKey="network:settings_admin_title" /></Overview.Header.Title>
            <div className="pull-right">
              <SearchBar value={search} onSearch={setSearch} debounce={false} filter={false} />
            </div>
            <div className="pull-right">
              <AdminsForm
                network={network}
                admins={admins}
                roles={roles}
                fetchGroups={fetchPermissionGroups}
                updateAdmin={updateUserRoles}
                onSuccess={fetchAdmins}
              >
                <Button type="primary" iconRight="add" size="large">
                  <Trans i18nKey="organisation:settings_admins_add_button" />
                </Button>
              </AdminsForm>
            </div>
          </Overview.Header>
          <Overview.Content>
            <Table
              columns={columns}
              items={search ? admins.filter((item) => item.full_name.toLowerCase().includes(search.toLowerCase())) : admins}
              renderRow={({ item }) => [
                <LinkWrapper url={`/networks/${network.id}/users/${item.id}/about`}><ProfileImage enablePopover size={35} user={item} /></LinkWrapper>,
                <LinkWrapper url={`/networks/${network.id}/users/${item.id}/about`} className="tw-text-inherit hover:tw-text-inherit hover:tw-underline">
                  <span className="fs-exclude">{item.full_name}</span>
                </LinkWrapper>,
                item.last_modified && (
                  <>
                    {item.last_modified.added_by && <div>{item.last_modified.added_by.full_name}</div>}
                    <small>{moment(item.last_modified.added_at).format('D MMMM YYYY')}</small>
                  </>
                ),
                mergeNamesText(item.roles, 3, 'name'),
              ]}
              placeholder={(
                <div className="Table__Placeholder">
                  <Trans i18nKey="network:settings_admin_table_placeholder" />
                </div>
              )}
              ActionComponent={({ item }) => {
                if (item.id === loggedUser.id ||
                  (item.roles.some((role) => role.is_global) && !permissions.includes(EPermissions.GLOBAL_NETWORK_ADMINS_REMOVE))
                ) return null;

                return (
                  <Dropdown alignRight toggle={<Button><Trans i18nKey="organisation:settings_roles_actions" /></Button>}>
                    <Dropdown.Item
                      onClick={() => setEditing(item.id)}
                    >
                      <Trans i18nKey="organisation:settings_admins_actions_edit" />
                    </Dropdown.Item>
                    <Dropdown.Divider />
                    <Confirm
                      title={t('organisation:settings_admins_actions_confirm_remove', { fullName: item.full_name })}
                      description={t('organisation:settings_admins_actions_confirm_remove_description')}
                      onConfirm={() => removeAdmin(item.id)}
                    >
                      <Dropdown.Item danger><Trans i18nKey="organisation:settings_admins_actions_remove" /></Dropdown.Item>
                    </Confirm>
                  </Dropdown>
                );
              }}
            />
          </Overview.Content>
        </Overview>
      </Container.Content>
    </Container>
  );
};

const mapStateToProps = (state) => ({
  loggedUser: userSelector.selected(state),
  permissions: userSelector.permissions(state),
  network: networkSelector.selected(state),
  admins: networkSelector.admins(state),
  roles: networkSelector.roles(state),
});

const mapDispatchToProps = {
  fetchRoles: require('../../actions').fetchRoles,
  fetchPermissionGroups: require('../../../organisation/actions').fetchPermissionGroups,
  removeAdmin: require('../../actions').removeAdmin,
  fetchAdmins: require('../../actions').fetchAdmins,
};

export default withTranslation()(connect(mapStateToProps, mapDispatchToProps)(
  pageWrapper(EEventNames.VISITED_NETWORK_ADMINS_SETTINGS_PAGE)(NetworkSettingsAdminsContainer),
));
